import { ArrowRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import { CURRENT_EXHIBITIONS, PAST_EXHIBITIONS, EXHIBITION_IMAGES } from "@/data/content";
import { useT, useScheduleTranslator } from "@/i18n";

export default function OurExhibitions() {
  const t = useT();
  const tr = useScheduleTranslator();
  return (
    <div data-testid="exhibitions-page" className="bg-white">
      <PageHero
        eyebrow={t.pages.exhibitions.eyebrow}
        title={t.pages.exhibitions.title}
        subtitle={t.pages.exhibitions.subtitle}
        image={EXHIBITION_IMAGES[5]}
      />

      {/* Current */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14">
          <Reveal className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="eyebrow">{t.pages.exhibitions.currentEyebrow}</p>
              <h2 className="heading-display mt-3 text-5xl sm:text-6xl uppercase">
                {t.pages.exhibitions.currentTitle1}{" "}
                <span className="text-[#C20E1A]">{t.pages.exhibitions.currentTitle2}</span>
              </h2>
            </div>
            <p className="max-w-sm text-sm text-black/60">{t.pages.exhibitions.currentBody}</p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {CURRENT_EXHIBITIONS.map((ex, i) => (
              <Reveal key={`${ex.city}-${i}`} delay={i * 40}>
                <article
                  data-testid={`exhibition-card-${i}`}
                  className="group bg-white border border-black/5 hover:border-[#C20E1A]/30 transition-colors"
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-black/5">
                    <img
                      src={ex.image}
                      alt={ex.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                    />
                    {ex.schedule && (
                      <div className="absolute top-4 left-4 bg-white/95 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold text-black">
                        {tr(ex.schedule)}
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    {ex.touring && (
                      <p className="text-[10px] uppercase tracking-[0.3em] text-[#C20E1A] font-bold mb-2">
                        {t.common.touringExhibition}
                      </p>
                    )}
                    <h3 className="text-lg lg:text-xl font-bold tracking-tight uppercase text-black">
                      {ex.title}
                    </h3>
                    <p className="mt-1 text-sm text-black/60">{ex.city}</p>
                    <p className="mt-4 text-xs text-black/55 leading-relaxed border-t border-black/10 pt-4">
                      {tr(ex.hours)}
                    </p>
                    <button
                      data-testid={`exhibition-visit-${i}`}
                      onClick={() => ex.link && window.open(ex.link, "_blank", "noopener,noreferrer")}
                      disabled={!ex.link}
                      className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-black hover:text-[#C20E1A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {t.common.visitNow} <ArrowRight size={14} />
                    </button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Past */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-20 bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.4em] text-[#C20E1A] font-bold">
              {t.pages.exhibitions.pastEyebrow}
            </p>
            <h2 className="heading-display mt-4 text-5xl sm:text-6xl uppercase">
              {t.pages.exhibitions.pastTitle1}{" "}
              <span className="text-[#C20E1A]">{t.pages.exhibitions.pastTitle2}</span>
            </h2>
          </Reveal>

          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-10 border-t border-white/10 pt-10">
            {PAST_EXHIBITIONS.map((e, i) => (
              <li
                key={i}
                data-testid={`past-card-${i}`}
                className="border-b border-white/10 pb-6 group"
              >
                <p className="text-2xl font-bold italic tracking-tight text-[#C20E1A]">{e.year}</p>
                <p className="mt-3 text-xl font-bold uppercase tracking-tight group-hover:text-[#C20E1A] transition-colors">
                  {e.title}
                </p>
                <p className="text-sm text-white/60">{e.city}</p>
                {e.note && (
                  <p className="mt-2 text-[11px] italic text-white/50 leading-relaxed">
                    * {e.note}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
